import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/AdminTestimonials.css";

type AboutStatus = "active" | "inactive";
type ModalMode = "add" | "edit" | "view";

interface AboutRow {
  id: number;
  section_key: string;
  block_key: string;
  title: string;
  subtitle: string;
  content: string;
  image: any;
  link: string;
  sort_order: number;
  status: AboutStatus;
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

export default function AdminAbout() {
  const { showModal } = useMessageModal();

  const [data, setData] = useState<AboutRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<AboutRow | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const aboutFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      {
        key: "section_key",
        label: "Section",
        type: "select",
        options: [
          { label: "Home Banner", value: "home_banner" },
          { label: "About P1 Left", value: "about_p1_left" },
          { label: "About P1 Right", value: "about_p1_right" },
          { label: "About P2", value: "about_p2" },
          { label: "Leadership", value: "leadership" },
          { label: "Infrastructure", value: "infrastructure" },
          { label: "Unique Value", value: "unique_value" },
        ],
        colSpan: 2,
      },
      { key: "block_key", label: "Block Key", colSpan: 2 },
      { key: "title", label: "Title", colSpan: 2 },
      { key: "subtitle", label: "Subtitle", colSpan: 2 },
      {
        key: "content",
        label: "Content",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        imageType: "single",
        imageFolder: "about",
        uploadField: "aboutImage",
        colSpan: 1,
      },
      { key: "link", label: "Link", colSpan: 2 },
      {
        key: "sort_order",
        label: "Sort Order",
        type: "number",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "section_key", label: "Section" },
    { key: "block_key", label: "Block Key" },
    { key: "title", label: "Title" },
    { key: "subtitle", label: "Subtitle" },
    {
      key: "image",
      label: "Image",
      imageFolder: "about",
    },
    {
      key: "content",
      label: "Content",
      render: (row) => (
        <span className="admin-testimonial-text-clamp">{row.content}</span>
      ),
    },
    { key: "sort_order", label: "Sort Order" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
    },
  ];

  const fetchAbout = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/about`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch About Error:", error);
    }
  };

  useEffect(() => {
    fetchAbout();
  }, []);

  const buildFormData = (
    formData: any,
    fields: FormField[],
    // activeRow?: any,
  ) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value?.removed || value === "") {
          form.append(field.key, "REMOVE");
        } else if (field.key !== "image") {
          form.append(field.key, value ?? "");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, aboutFields);

      const method = modalMode === "add" ? "POST" : "PUT";
      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/about`
          : `${BASE_URL}/admin/about/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (result.success) {
        fetchAbout();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "About record added successfully."
              : "About record updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save about record.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving the about record.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete About Record",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/about/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchAbout();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "About record deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="About"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="about"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="About"
        fields={aboutFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
